<template>
    <div class="container-nzidoo py-5">
        <!-- Loading Overlay -->
        <div v-if="loader" id="opaque"></div>
        <div v-if="loader" class="loader"></div>

        <!-- Success Message -->
        <div v-if="product.name" class="success-banner mb-5">
            <div class="success-content">
                <div class="success-icon-wrapper">
                    <i class="fa fa-check-circle success-icon"></i>
                </div>
                <div>
                    <h3 class="mb-1 font-weight-bold text-light">Product Created Successfully!</h3>
                    <p class="mb-0 text-light">Your product has been added to the catalog</p>
                </div>
            </div>
        </div>

        <!-- Product Details Section -->
        <div v-if="product.name" class="row">
            <!-- Product Images Column -->
            <div class="col-12 col-lg-5 mb-4 mb-lg-0">
                <!-- Main Product Image -->
                <div v-if="imagePreview.length > 0" class="main-image-wrapper mb-3">
                    <img class="main-product-image" :src="storageUrl + imagePreview[0]" alt="Product Image" />
                    <div class="image-badge">
                        <i class="fa fa-search-plus"></i>
                    </div>
                </div>

                <!-- Product Thumbnails -->
                <div v-if="imagePreview.length > 1" class="thumbnails-grid">
                    <div v-for="(image, index) in imagePreview" :key="index" class="thumbnail-item">
                        <img :src="storageUrl + image" alt="Product Thumbnail" class="thumbnail-image" />
                    </div>
                </div>
            </div>

            <!-- Product Summary Column -->
            <div class="col-12 col-lg-7">
                <div class="product-details-card">
                    <div class="card-header-custom">
                        <h4 class="mb-0 font-weight-bold">{{ $t("ecommerce.product.resume") }}</h4>
                    </div>

                    <div class="details-list">
                        <div class="detail-row">
                            <span class="detail-label">{{ $t("ecommerce.product.name") }}</span>
                            <span class="detail-value font-weight-bold">{{ product.name }}</span>
                        </div>

                        <div class="detail-row">
                            <span class="detail-label">{{ $t("main.category") }}</span>
                            <span class="detail-value">{{ product.category.name }}</span>
                        </div>

                        <div class="detail-row">
                            <span class="detail-label">{{ $t("ecommerce.sales-type") }}</span>
                            <span class="detail-value">{{ $t(`salesType.${product.sales_type}`) }}</span>
                        </div>

                        <div class="detail-row highlight-row" v-if="product.starting_price || product.fixed_price">
                            <span class="detail-label">{{ $t("ecommerce.product.startingFrom") }}</span>
                            <span class="detail-value price-value">
                                {{ (product.starting_price || product.fixed_price) | currency(" DA", 0, {
                                    symbolOnLeft: false
                                }) }}
                            </span>
                        </div>

                        <div class="detail-row" v-if="product.lowest_price">
                            <span class="detail-label">{{ $t("ecommerce.product.reservedPrice") }}</span>
                            <span class="detail-value">
                                {{ product.lowest_price | currency(" DA", 0, { symbolOnLeft: false }) }}
                            </span>
                        </div>

                        <div class="detail-row">
                            <span class="detail-label">{{ $t("ecommerce.product.acceptOffers") }}</span>
                            <span class="detail-value">
                                <span v-if="product.accept_offer" class="modern-badge badge-success">
                                    <i class="fa fa-check mr-1"></i>{{ $t("general.yes") }}
                                </span>
                                <span v-else class="modern-badge badge-secondary">
                                    <i class="fa fa-times mr-1"></i>{{ $t("general.no") }}
                                </span>
                            </span>
                        </div>

                        <div class="detail-row" v-if="product.state">
                            <span class="detail-label">{{ $t("ecommerce.product.state") }}</span>
                            <span class="detail-value">{{ product.state }}</span>
                        </div>

                        <div class="detail-row" v-if="product.quantity">
                            <span class="detail-label">{{ $t("ecommerce.product.quantity") }}</span>
                            <span class="detail-value">
                                <span class="quantity-badge">{{ product.quantity }}</span>
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Action Buttons -->
        <div v-if="product.name" class="row mt-5 mb-5">
            <div class="col-12 col-md-6 mx-auto">
                <button type="button" class="btn-modern btn-modern-primary" @click="goToProductSell">
                    <i class="fa fa-plus-circle mr-2"></i>
                    {{ $t('sentence.create_new_products') }}
                </button>
            </div>
        </div>

        <!-- Error State -->
        <div v-if="!loader && !product.name" class="error-state">
            <div class="error-icon-wrapper">
                <i class="fa fa-exclamation-triangle"></i>
            </div>
            <h4 class="mt-3 mb-2">Product Not Found</h4>
            <p class="text-muted mb-4">Unable to load product information.</p>
            <button type="button" class="btn-modern btn-modern-secondary" @click="goBack">
                <i class="fa fa-arrow-left mr-2"></i>
                Go Back
            </button>
        </div>

        <!-- Notifications -->
        <notifications style="top:5%;" width="35%" group="error" position="top center" />
    </div>
</template>

<script>
import config from "@/config";
export default {
    name: "ProductCreatedSuccessComp",
    data() {
        return {
            product: {},
            imagePreview: [],
            loader: false,
            storageUrl: config.storageUrl,
            categories: [],
        };
    },
    async mounted() {
        await this.loadProduct();
    },
    methods: {
        async loadProduct() {
            this.loader=true;
            try {
                const id=this.$route.params.id;

                if (!id) {
                    this.$notify({
                        group: "error",
                        title: "Error",
                        type: "error",
                        text: "Product ID is missing.",
                    });
                    this.loader=false;
                    return;
                }

                const response=await this.$axios.get(`product/get/${id}`);
                this.product=response.data;

                if (this.$store.state.categories&&this.$store.state.categories.categories) {
                    this.categories=this.$store.state.categories.categories;
                }

                if (this.product.pictures&&this.product.pictures.length>0) {
                    this.imagePreview=this.product.pictures.map(pic => {
                        if (typeof pic==='string') {
                            return pic;
                        }
                        return pic.url||pic.path||'';
                    });
                }

            } catch (error) {
                console.error('Error loading product:', error);
                this.$notify({
                    group: "error",
                    title: "Error",
                    type: "error",
                    text: "Unable to load product information.",
                });
            } finally {
                this.loader=false;
            }
        },

        goToProductSell() {
            this.$router.push({ name: 'productSell' });
        },

        goBack() {
            this.$router.go(-1);
        },
    },
};
</script>

<style scoped>
/* Loading Overlay */
#opaque {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1000;
    background-color: rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(4px);
}

.loader {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1001;
    border: 4px solid rgba(255, 255, 255, 0.2);
    border-top: 4px solid var(--color-nz);
    border-radius: 50%;
    width: 60px;
    height: 60px;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    0% {
        transform: translate(-50%, -50%) rotate(0deg);
    }

    100% {
        transform: translate(-50%, -50%) rotate(360deg);
    }
}

/* Success Banner */
.success-banner {
    background: linear-gradient(135deg, var(--success-300) 0%, var(--teal) 100%);
    border-radius: 16px;
    padding: 2rem;
    box-shadow: 0 10px 30px rgba(40, 167, 69, 0.2);
    animation: slideDown 0.5s ease-out;
}

@keyframes slideDown {
    from {
        opacity: 0;
        transform: translateY(-20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.success-content {
    display: flex;
    align-items: center;
    color: white;
}

.success-icon-wrapper {
    margin-right: 1.5rem;
}

.success-icon {
    font-size: 3rem;
    animation: scaleIn 0.5s ease-out 0.2s both;
}

@keyframes scaleIn {
    from {
        transform: scale(0);
    }

    to {
        transform: scale(1);
    }
}

/* Main Image */
.main-image-wrapper {
    position: relative;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    background: var(--gray-100);
}

.main-product-image {
    width: 100%;
    height: auto;
    display: block;
    transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.main-image-wrapper:hover .main-product-image {
    transform: scale(1.05);
}

.image-badge {
    position: absolute;
    top: 16px;
    right: 16px;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    padding: 10px 14px;
    border-radius: 10px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    cursor: pointer;
    transition: all 0.3s ease;
}

.image-badge:hover {
    transform: scale(1.1);
    background: white;
}

.image-badge i {
    color: var(--color-nz);
    font-size: 1.1rem;
}

/* Thumbnails */
.thumbnails-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
}

.thumbnail-item {
    position: relative;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    cursor: pointer;
    transition: all 0.3s ease;
}

.thumbnail-item:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

.thumbnail-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: opacity 0.3s ease;
}

.thumbnail-item:hover .thumbnail-image {
    opacity: 0.8;
}

/* Product Details Card */
.product-details-card {
    background: white;
    border-radius: 16px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
    overflow: hidden;
}

.card-header-custom {
    padding: 1.75rem 2rem;
    background: linear-gradient(135deg, var(--gray-100) 0%, var(--gray-200) 100%);
    border-bottom: 1px solid var(--gray-300);
}

.details-list {
    padding: 0.5rem 0;
}

.detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.25rem 2rem;
    border-bottom: 1px solid var(--gray-100);
    transition: background-color 0.2s ease;
}

.detail-row:last-child {
    border-bottom: none;
}

.detail-row:hover {
    background-color: var(--gray-50);
}

.detail-label {
    color: var(--gray-600);
    font-size: 0.95rem;
    font-weight: 500;
}

.detail-value {
    color: var(--gray-900);
    font-size: 1rem;
    text-align: right;
}

.highlight-row {
    background: linear-gradient(135deg, var(--primary-light-200) 0%, var(--primary-light-100) 100%);
}

.highlight-row:hover {
    background: linear-gradient(135deg, var(--primary-light) 0%, var(--primary-light-200) 100%);
}

.price-value {
    color: var(--color-nz);
    font-size: 1.5rem;
    font-weight: 700;
}

/* Modern Badges */
.modern-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-size: 0.875rem;
    font-weight: 600;
    transition: all 0.3s ease;
}

.modern-badge.badge-success {
    background: linear-gradient(135deg, var(--success-300) 0%, var(--teal) 100%);
    color: white;
}

.modern-badge.badge-secondary {
    background: linear-gradient(135deg, var(--gray-600) 0%, var(--gray-700) 100%);
    color: white;
}

.quantity-badge {
    display: inline-block;
    background: linear-gradient(135deg, var(--color-nz) 0%, var(--color-nz-200) 100%);
    color: white;
    padding: 0.4rem 1rem;
    border-radius: 20px;
    font-weight: 600;
    font-size: 0.875rem;
}

/* Modern Buttons */
.btn-modern {
    width: 100%;
    padding: 1rem 2rem;
    border: none;
    border-radius: 12px;
    font-size: 1.1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
}

.btn-modern-primary {
    background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
    color: white;
}

.btn-modern-primary:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 123, 255, 0.3);
}

.btn-modern-secondary {
    background: linear-gradient(135deg, #6c757d 0%, #5a6268 100%);
    color: white;
}

.btn-modern-secondary:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(108, 117, 125, 0.3);
}

/* Error State */
.error-state {
    text-align: center;
    padding: 4rem 2rem;
    background: white;
    border-radius: 16px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
}

.error-icon-wrapper {
    width: 80px;
    height: 80px;
    margin: 0 auto;
    background: linear-gradient(135deg, var(--color-oo) 0%, var(--color-oo-100) 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.error-icon-wrapper i {
    font-size: 2.5rem;
    color: white;
}

/* Responsive */
@media (max-width: 767px) {
    .success-banner {
        padding: 1.5rem;
    }

    .success-content {
        flex-direction: column;
        text-align: center;
    }

    .success-icon-wrapper {
        margin-right: 0;
        margin-bottom: 1rem;
    }

    .card-header-custom,
    .detail-row {
        padding: 1rem 1.25rem;
    }

    .thumbnails-grid {
        grid-template-columns: repeat(3, 1fr);
    }

    .detail-row {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.5rem;
    }

    .detail-value {
        text-align: left;
    }
}

@media (max-width: 575px) {
    .thumbnails-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}
</style>